import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

import { VisibilitySliceType } from '../components/enums';
import type { ContactFormFieldSlice, Settings } from '../components/types';

dayjs.extend(isBetween);

const TIME_FORMAT_PATTERN = 'HH:mm';

const isReasonDisabled = (slices: ContactFormFieldSlice[]) =>
  slices.some(({ type, disabled }) => type === VisibilitySliceType.CallbackSolution && disabled);

const getTodayDateWithPredefinedTime = (time: dayjs.Dayjs) =>
  dayjs().set('hour', time.hour()).set('minute', time.minute()).set('second', time.second());

/**
 * This utility function determines whether the current time falls
 * within the opening and closing hours of the customer service team,
 * and additionally verifies if the current day is designated as a
 * disabled workday or holiday.
 *
 * @param settings time rules that indicate when the callback solution is available
 * @returns whether the channel selection step is available or not
 */
export const getChannelSelectionAvailability = (
  slices: ContactFormFieldSlice[],
  { channels }: Settings
) => {
  if (channels == null || !channels.isEnabled) {
    return false;
  }

  // Checks if the channel selection step is disabled for the current reason
  if (isReasonDisabled(slices)) {
    return false;
  }

  const { openingTime, closingTime, disabledWeekdays, disabledHolidays } = channels;

  const now = dayjs();
  const opening = getTodayDateWithPredefinedTime(dayjs(openingTime));
  const closing = getTodayDateWithPredefinedTime(dayjs(closingTime));

  if (!now.isBetween(opening, closing, null, '[]')) {
    return false;
  }

  const isDisabledWeekday = disabledWeekdays?.some(day => {
    const weekday = day?.weekday ? Number(day.weekday) : null;

    return weekday !== null && dayjs().day(weekday).isSame(now, 'day');
  });

  const isDisabledHoliday = disabledHolidays?.some(day => {
    const holiday = day?.date ?? null;

    return holiday !== null && dayjs(holiday).isSame(now, 'day');
  });

  return isDisabledWeekday || isDisabledHoliday ? false : true;
};

export const formatTime = (time: string) => {
  if (!time) {
    return '';
  }

  return dayjs(time).format(TIME_FORMAT_PATTERN);
};
